import dateformat from 'dateformat';
import { getDateFromYYYYMMDDString } from '../../utils/common/utils';
import sendDataToNewrelic from '@stencil/send-data-to-new-relic';
import {
  getFlightDetails,
  getPriceString,
  sortMseByPrice,
  getMessageList,
  getTotalMinutesFromDuration
} from '@stencil/flights-utils/src/srp-thor-utils';
import getDateObject from '@stencil/flights-date-utils/src/get-date-object';
import { pureFilterFunction, impureFilterFunction } from './filterUtils';

export const getTempSrcDestObj = (airports, iataFromUrl) => {
  for (let index = 0; index < airports.length; index++) {
    let currAirport = airports[index];
    if (currAirport.i === iataFromUrl) {
      return {
        cc: currAirport.d,
        cnty: currAirport.c,
        ct: currAirport.t,
        iata: currAirport.i,
        n: currAirport.n
      };
    }
  }
};

function getCardDateObject(dateObj) {
  if (!dateObj) {
    return null;
  }
  return {
    dateObj: dateObj.toString(),
    day: dateformat(dateObj, 'dddd'),
    date: dateformat(dateObj, 'dd'),
    dateOnCard: dateformat(dateObj, 'ddd, dd mmm')
  };
}

function getDurationForDisplay(dur) {
  return (dur / 100).toFixed(2).replace('.', 'h ') + 'm';
}

// function getPriceString(price) {
//   return price
//     .toLocaleString('en-IN', {
//       maximumFractionDigits: 2,
//       style: 'currency',
//       currency: 'INR'
//     })
//     .replace('.00', '')
//     .replace('₹', '')
//     .trim();
// }

const getAlternateTimeText = (flight, datesArr, source, destination, departure, arrival) => {
  let onwardAlternateText = '';
  let returnAlternateText = '';

  const onwardDate = datesArr[flight.f[0]['1']].toString();
  if (onwardDate !== departure.yyyymmdd) {
    const onwardDateObj = getCardDateObject(getDateFromYYYYMMDDString(onwardDate));
    onwardAlternateText = `Fly on ${onwardDateObj.dateOnCard}`;
  }
  if (flight.r) {
    const returnDate = datesArr[flight.r.f[0]['1']].toString();
    if (returnDate !== arrival.yyyymmdd) {
      const returnDateObj = getCardDateObject(getDateFromYYYYMMDDString(returnDate));
      returnAlternateText = `Return on ${returnDateObj.dateOnCard}`;
    }
  }
  if (onwardAlternateText !== '' && returnAlternateText !== '') {
    return `${onwardAlternateText} and ${returnAlternateText}`;
  } else if (onwardAlternateText !== '') {
    return onwardAlternateText;
  }
  return returnAlternateText;
};

const getAlternateLocationText = (flight, airportsArr, source, destination) => {
  if (flight.nd) {
    const from = airportsArr[flight.f[0].u];
    const to = airportsArr[flight.f[flight.f.length - 1].g];
    return `Fly ${
      flight.nf === 1
        ? `from ${from.t}, approx ${flight.nd}KM from ${source.iata}`
        : `to ${to.t}, approx ${flight.nd}KM from ${destination.iata}`
    }`;
  } else if (flight.r && flight.r.nd) {
    const from = airportsArr[flight.r.f[0].u];
    const to = airportsArr[flight.r.f[flight.r.f.length - 1].g];
    return `Fly ${
      flight.nf === 1
        ? `from ${from.t}, approx ${flight.nd}KM from ${destination.iata}`
        : `to ${to.t}, approx ${flight.nd}KM from ${source.iata}`
    }`;
  }
};

const getAlternateSaveText = flight => {
  let saveText = '';
  let hasFareSave = false;
  if (flight.fs) {
    saveText = getPriceString(flight.fs);
    hasFareSave = true;
  } else if (flight.ts) {
    saveText = getDurationForDisplay(flight.ts);
  }
  return {
    saveText,
    hasFareSave
  };
};

const getAlternateFlightDetails = (flight, addInfo) => {
  if (flight.alternateTime) {
    const { datesArr, source, destination, departure, arrival } = addInfo;
    const alternateText = getAlternateTimeText(
      flight,
      datesArr,
      source,
      destination,
      departure,
      arrival
    );
    const { saveText, hasFareSave } = getAlternateSaveText(flight);
    return {
      alternateText,
      saveText,
      hasFareSave
    };
  } else {
    const { airportsArr, source, destination } = addInfo;
    const alternateText = getAlternateLocationText(flight, airportsArr, source, destination);
    const { saveText, hasFareSave } = getAlternateSaveText(flight);
    return {
      alternateText,
      saveText,
      hasFareSave
    };
  }
};

export function formatFlightForDisplay(displayInformation) {
  const {
    flightList,
    refundabilityArr,
    airportsArr,
    carriersArr,
    datesArr,
    textArr,
    valueArr,
    iconArr,
    discountFares,
    discountMessages,
    originalIndex,
    showOnePaxPrice,
    source,
    destination,
    departure,
    arrival,
    fareFamilyOrder,
    specialFareCallBack
  } = displayInformation;
  const flight = flightList[0];
  const onwardFlight = getFlightDetails({
    flight,
    refundabilityArr,
    airportsArr,
    carriersArr,
    datesArr,
    valueArr,
    iconArr,
    discountFares,
    discountMessages,
    originalIndex,
    showOnePaxPrice
  });
  const returnFlight = flight.r
    ? getFlightDetails({
        flight: flight.r,
        refundabilityArr,
        airportsArr,
        carriersArr,
        datesArr,
        valueArr,
        iconArr,
        discountFares,
        discountMessages,
        originalIndex,
        showOnePaxPrice
      })
    : null;

  const priceToShow = showOnePaxPrice ? 'p1' : 'p';
  let ancillaryList = undefined;
  if (flight.al && flight.al.length > 0) {
    ancillaryList = flight.al.map(ancillary => {
      const header = valueArr[ancillary.n];
      const headerIcon = iconArr[ancillary.ti] || '';
      const detailsList = [];
      const detailsLen = ancillary.t?.length || 0;
      const refundable = refundabilityArr[ancillary.w];
      for (let i = 0; i < detailsLen; i++) {
        const temp = {
          t: typeof ancillary.t[i] !== 'undefined' ? textArr[ancillary.t[i]] : '',
          v: typeof ancillary.v[i] !== 'undefined' ? valueArr[ancillary.v[i]] : '',
          i: typeof ancillary.i[i] !== 'undefined' ? iconArr[ancillary.i[i]] : '',
          isCommon: false
        };
        detailsList.push(temp);
      }
      const commonList = (flight.ac && flight.ac.t && flight.ac.t.length) || 0;
      for (let i = 0; i < commonList; i++) {
        const temp = {
          t: typeof flight.ac.t[i] !== 'undefined' ? textArr[flight.ac.t[i]] : '',
          v: typeof flight.ac.v[i] !== 'undefined' ? valueArr[flight.ac.v[i]] : '',
          i: typeof flight.ac.i[i] !== 'undefined' ? iconArr[flight.ac.i[i]] : '',
          isCommon: true
        };
        detailsList.push(temp);
      }
      const bundleAmt = getPriceString(parseInt(ancillary.af, 10));
      const slashedAmt = ancillary.sa ? getPriceString(parseInt(ancillary.sa, 10)) : undefined;
      const bundleAmtNonFormatted = parseInt(ancillary.af, 10);
      const tag = typeof ancillary.b !== 'undefined' ? iconArr[ancillary.b] : '';
      //const hasPriceLock = ancillary?.li?.a || false;
      const persuasionText = valueArr[ancillary.pt] || '';
      const fareKey = ancillary?.fk || '';
      return {
        header,
        headerIcon,
        detailsList,
        bundleAmt,
        slashedAmt,
        bundleAmtNonFormatted,
        tag,
        //hasPriceLock,
        persuasionText,
        refundable,
        fareKey,
        persuasionBackgroundColor: ancillary.pbg || [],
        isCustomFareFamily: ancillary.ic || false
      };
    });
  }

  const isMSE = flightList.length > 1;
  const isExtraSeatFlight = flight.st && (flight.st === 7 || flight.st === 8);
  const extraSeatType = isExtraSeatFlight ? flight.st : null;

  let mseList = undefined;
  if (flightList.length > 1) {
    mseList = flightList.sort(sortMseByPrice).map((flightItem, index) => {
      const pricePerPerson = getPriceString(flightItem.p1);
      const price = getPriceString(flightItem.p);
      const priceToDisplay = getPriceString(flightItem[priceToShow]);
      let discountedFare = undefined;
      let disMessage = undefined;
      let discount = undefined;
      if (flightItem[5] && flightItem[7] && flightItem[8]) {
        disMessage = discountMessages[flightItem[8]];
        discountedFare = getPriceString(flightItem[5]);
        discount = getPriceString(discountFares[flightItem[7]]);
      }
      const messageList = flightItem.ml
        ? getMessageList(
            flightItem.ml.t,
            textArr,
            flightItem.ml.v,
            valueArr,
            flightItem.ml.i,
            iconArr
          )
        : [];
      // flightItem.ml &&
      // flightItem.ml.t &&
      // flightItem.ml.v &&
      // flightItem.ml.t.map((key, index) => {
      //   return textArr[key].replace('%s', valueArr[flightItem.ml.v[index]]);
      // });
      return {
        messageList,
        pricePerPerson,
        price,
        priceToDisplay,
        ...(disMessage ? { disMessage } : null),
        ...(discountedFare ? { discountedFare } : null),
        ...(discount ? { discount } : null),
        fareText: `Fare ${index + 1}`
      };
    });
  }

  let alternateFlightInfo = undefined;
  if (flight.alternateTime) {
    alternateFlightInfo = getAlternateFlightDetails(flight, {
      datesArr,
      source,
      destination,
      departure,
      arrival
    });
  } else if (flight.alternateLocation) {
    alternateFlightInfo = getAlternateFlightDetails(flight, { airportsArr, source, destination });
  }

  const flightInfo = {
    ...onwardFlight,
    ...(isMSE ? { isMSE } : null),
    ...(returnFlight ? { returnFlight } : null),
    ...(ancillaryList ? { ancillaryList } : null),
    ...(mseList ? { mseList } : null),
    ...(flight.alternateTime ? { alternateTime: true } : null),
    ...(flight.alternateLocation ? { alternateLocation: true } : null),
    ...(alternateFlightInfo ? { ...alternateFlightInfo } : null),
    ...(isExtraSeatFlight ? { isExtraSeatFlight } : null),
    ...(extraSeatType ? { extraSeatType } : null),
    ...(fareFamilyOrder && fareFamilyOrder.length ? { fareFamilyOrder } : null)
  };

  if (flight.psl) {
    specialFareCallBack(originalIndex);
  }

  return flightInfo;
}

export function sortMseFlights(onwardFlights) {
  return onwardFlights.map(flightItem => {
    if (flightItem.length > 1) {
      return flightItem.sort(sortMseByPrice);
    }
    return flightItem;
  });
}

export const sortByBookability = {
  asc: function (a, b) {
    return a.valuesForSort.recommended - b.valuesForSort.recommended;
  }
};

export const sortByPrice = {
  asc: function (a, b) {
    if (a.flightArr.length > 1 || b.flightArr.length > 1) {
      if (a.valuesForSort.cost === b.valuesForSort.cost) {
        if (a.valuesForSort.duration === b.valuesForSort.duration) {
          return a.valuesForSort.departure - b.valuesForSort.departure;
        }
        return a.valuesForSort.duration - b.valuesForSort.duration;
      }
      return a.valuesForSort.cost - b.valuesForSort.cost;
    } else {
      if (a.valuesForSort.cost === b.valuesForSort.cost) {
        if (a.valuesForSort.departure === b.valuesForSort.departure) {
          return a.valuesForSort.duration - b.valuesForSort.duration;
        }
        return a.valuesForSort.departure - b.valuesForSort.departure;
      }
      return a.valuesForSort.cost - b.valuesForSort.cost;
    }
  },
  des: function (a, b) {
    return b.valuesForSort.cost - a.valuesForSort.cost;
  }
};

export const sortByDuration = {
  asc: function (a, b) {
    return a.valuesForSort.duration - b.valuesForSort.duration;
  },
  des: function (a, b) {
    return b.valuesForSort.duration - a.valuesForSort.duration;
  }
};

export const sortByDeparture = {
  asc: function (a, b) {
    return a.valuesForSort.departure - b.valuesForSort.departure;
  },
  des: function (a, b) {
    return b.valuesForSort.departure - a.valuesForSort.departure;
  }
};

export const sortByArrival = {
  asc: function (a, b) {
    return a.valuesForSort.arrival - b.valuesForSort.arrival;
  },
  des: function (a, b) {
    return b.valuesForSort.arrival - a.valuesForSort.arrival;
  }
};

export const sortDestinationsByPrice = {
  asc: function (a, b) {
    return a.minCostStop.price - b.minCostStop.price;
  },
  des: function (a, b) {
    return b.minCostStop.price - a.minCostStop.price;
  }
};

export const sortDestinationsByDuration = {
  asc: function (a, b) {
    return (
      parseInt(a.minCostStop.onwardFlightDurationInMins) +
      parseInt(a.minCostStop?.returnFlightDurationInMins || '0') -
      (parseInt(b.minCostStop.onwardFlightDurationInMins) +
        parseInt(b.minCostStop?.returnFlightDurationInMins || '0'))
    );
  },
  des: function (a, b) {
    return (
      parseInt(b.minCostStop.onwardFlightDurationInMins) +
      parseInt(b.minCostStop?.returnFlightDurationInMins || '0') -
      (parseInt(a.minCostStop.onwardFlightDurationInMins) +
        parseInt(a.minCostStop?.returnFlightDurationInMins || '0'))
    );
  }
};

const sortFunctionDict = {
  recommended: sortByBookability,
  cheapest: sortByPrice,
  shortest: sortByDuration,
  earliest: sortByDeparture,
  earliestarrival: sortByArrival
};

const sortFunctionDestinationsDict = {
  cheapest: sortDestinationsByPrice,
  shortest: sortDestinationsByDuration
};

function sortList(sortFunction, list) {
  return list.sort(sortFunction);
}

export function sortFunction(listToBeSorted, sortSettings, isDestinations = false) {
  try {
    if (!isDestinations) {
      return sortList(
        sortFunctionDict[sortSettings.currentSort][sortSettings.isAscending ? 'asc' : 'des'],
        listToBeSorted
      );
    }
    return sortList(
      sortFunctionDestinationsDict[sortSettings.currentSort][
        sortSettings.isAscending ? 'asc' : 'des'
      ],
      listToBeSorted
    );
  } catch (err) {
    sendDataToNewrelic(`SortFunction - ${sortSettings.currentSort}`, `Failed with error: ${err}`);
  }
}

export function getSortOptionList(displayOnwardFlights, sortOption) {
  const sortOptionList = sortFunction(displayOnwardFlights, {
    currentSort: sortOption,
    isAscending: true,
    previousSortType: ''
  });
  return sortOptionList;
}

export function getSortOptionDetails(flightsList) {
  const sortOptionsDetails = {};
  const sortOptions = ['earliest', 'shortest', 'earliestarrival', 'cheapest', 'recommended'];
  if (!flightsList || flightsList.length === 0) {
    return {};
  }
  for (let i = 0; i < sortOptions.length; i++) {
    const flightsListClone = [...flightsList];
    const sortedList = getSortOptionList(flightsListClone, sortOptions[i]);
    if (sortOptions[i] === 'earliest') {
      const depTime = sortedList[0]?.flightArr[0].depTime;
      sortOptionsDetails[sortOptions[i]] = `earliest @ ${depTime}`;
    }
    if (sortOptions[i] === 'shortest') {
      const duration = (sortedList[0]?.duration).replace('h', 'hrs');
      sortOptionsDetails[sortOptions[i]] = `fastest @ ${duration}`;
    }
    if (sortOptions[i] === 'earliestarrival') {
      const currDate = new Date();
      const currDateString = getDateObject(currDate).dddddmmm;
      const arrTime = sortedList[0]?.arrivalDetails?.arrTime || '';
      const dateText =
        currDateString ===
        sortedList[0]?.flightArr[sortedList[0].flightArr.length - 1].arrDate.dateOnCard
          ? 'today'
          : sortedList[0]?.flightArr[sortedList[0].flightArr.length - 1].arrDate.dateOnCard;
      sortOptionsDetails[sortOptions[i]] = `${dateText} @ ${arrTime}`;
    }
    if (sortOptions[i] === 'cheapest') {
      const price = sortedList[0]?.price;
      sortOptionsDetails[sortOptions[i]] = `cheapest @ ${price}`;
    }
    if (sortOptions[i] === 'recommended') {
      const duration = (sortedList[0]?.duration).replace('h', 'hrs');
      const noOfStops = sortedList[0]?.flightArr.length - 1;
      const price = sortedList[0]?.price;
      sortOptionsDetails[sortOptions[i]] = `${duration}, ${noOfStops} stop${
        noOfStops !== 1 ? 's' : ''
      } - ${price}`;
    }
  }
  return sortOptionsDetails;
}

export function filterFunction(list, filters) {
  try {
    const filterKeys = Object.keys(filters);
    const filtered = list.filter(listItem => {
      return filterKeys.every(key => {
        if (filters[key].values.length < 1) return true;
        const flag = filters[key].filterFunc(listItem, filters[key].values);
        return flag;
      });
    });
    return filtered;
  } catch (err) {
    sendDataToNewrelic('FilterFunction', `Failed with error: ${err}`);
  }
}

export function filterPureImpureFlights(list, filters, carrierFilter) {
  const pureFlights = [];
  const impureFlights = [];
  try {
    const filterKeys = Object.keys(filters);
    list.forEach(listItem => {
      const isAllFilterPassed = filterKeys.every(key => {
        if (filters[key].values.length < 1) return true;
        return filters[key].filterFunc(listItem, filters[key].values);
      });
      if (isAllFilterPassed) {
        const isPure = pureFilterFunction(listItem, carrierFilter);
        const isImpure = !isPure && impureFilterFunction(listItem, carrierFilter);
        isPure && pureFlights.push(listItem);
        isImpure && impureFlights.push(listItem);
      }
    });
  } catch (err) {
    sendDataToNewrelic('FilterFunction', `Failed with error: ${err}`);
  } finally {
    return [pureFlights, impureFlights];
  }
}

export function createDisplayFlights(
  sourceList,
  displayInformation,
  currentLength,
  specialFareCallBack = index => {}
) {
  const displayFlights = sourceList.map((flight, index) =>
    formatFlightForDisplay({
      flightList: flight,
      ...displayInformation,
      originalIndex: currentLength + index,
      specialFareCallBack
    })
  );
  return displayFlights;
}

export function getFilterMinMaxValues(flightItem, filterMinMaxValues, journey, price, index) {
  try {
    if (flightItem.f.length > 1) {
      let multiFlag = false;
      for (let i = 1; i < flightItem.f.length; i++) {
        if (flightItem.f[i].c !== flightItem.f[i - 1].c) {
          multiFlag = true;
          break;
        }
      }
      if (multiFlag) {
        if (filterMinMaxValues.carriers['multi']) {
          if (filterMinMaxValues.carriers['multi'][journey]) {
            if (price < filterMinMaxValues.carriers['multi'][journey].price) {
              filterMinMaxValues.carriers['multi'][journey].price = price;
              filterMinMaxValues.carriers['multi'][journey].index = index;
            }
          } else {
            filterMinMaxValues.carriers['multi'][journey] = {
              price: null,
              index: null
            };
            filterMinMaxValues.carriers['multi'][journey].price = price;
            filterMinMaxValues.carriers['multi'][journey].index = index;
          }
        } else {
          filterMinMaxValues.carriers['multi'] = {
            [journey]: {
              price: null,
              index: null
            }
          };
          filterMinMaxValues.carriers['multi'][journey].price = price;
          filterMinMaxValues.carriers['multi'][journey].index = index;
        }
      } else {
        if (filterMinMaxValues.carriers[flightItem.f[0].c]) {
          if (filterMinMaxValues.carriers[flightItem.f[0].c][journey]) {
            if (price < filterMinMaxValues.carriers[flightItem.f[0].c][journey].price) {
              filterMinMaxValues.carriers[flightItem.f[0].c][journey].price = price;
              filterMinMaxValues.carriers[flightItem.f[0].c][journey].index = index;
            }
          } else {
            filterMinMaxValues.carriers[flightItem.f[0].c][journey] = {
              price: null,
              index: null
            };
            filterMinMaxValues.carriers[flightItem.f[0].c][journey].price = price;
            filterMinMaxValues.carriers[flightItem.f[0].c][journey].index = index;
          }
        } else {
          filterMinMaxValues.carriers[flightItem.f[0].c] = {
            [journey]: {
              price: null,
              index: null
            }
          };
          filterMinMaxValues.carriers[flightItem.f[0].c][journey].price = price;
          filterMinMaxValues.carriers[flightItem.f[0].c][journey].index = index;
        }
      }
    } else {
      if (filterMinMaxValues.carriers[flightItem.f[0].c]) {
        if (filterMinMaxValues.carriers[flightItem.f[0].c][journey]) {
          if (price < filterMinMaxValues.carriers[flightItem.f[0].c][journey].price) {
            filterMinMaxValues.carriers[flightItem.f[0].c][journey].price = price;
            filterMinMaxValues.carriers[flightItem.f[0].c][journey].index = index;
          }
        } else {
          filterMinMaxValues.carriers[flightItem.f[0].c][journey] = {
            price: null,
            index: null
          };
          filterMinMaxValues.carriers[flightItem.f[0].c][journey].price = price;
          filterMinMaxValues.carriers[flightItem.f[0].c][journey].index = index;
        }
      } else {
        filterMinMaxValues.carriers[flightItem.f[0].c] = {
          [journey]: {
            price: null,
            index: null
          }
        };
        filterMinMaxValues.carriers[flightItem.f[0].c][journey].price = price;
        filterMinMaxValues.carriers[flightItem.f[0].c][journey].index = index;
      }
    }
    return filterMinMaxValues;
  } catch (err) {
    sendDataToNewrelic('GetFilterMinMaxValues', `Failed with error: ${err}`);
  }
}

export function parseDomesticRoundTripDiscounts(discountObj) {
  const onwardDiscounts = {};
  const returnDiscounts = {};
  Object.entries(discountObj).forEach(([key, value]) => {
    if (key.indexOf('o') !== -1 && key.indexOf('r') !== -1) {
      const onwardIndex = parseInt(key.split('o')[1].split('r')[0]);
      const returnIndex = parseInt(key.split('r')[1]);
      const returnDiscountArr =
        onwardDiscounts[onwardIndex] && onwardDiscounts[onwardIndex].returnIndex
          ? onwardDiscounts[onwardIndex].returnIndex
          : [];
      onwardDiscounts[onwardIndex] = {
        ...onwardDiscounts[onwardIndex],
        returnIndex: [
          ...returnDiscountArr,
          {
            index: returnIndex,
            discount: value
          }
        ]
      };
      const onwardDiscountArr =
        returnDiscounts[returnIndex] && returnDiscounts[returnIndex].onwardIndex
          ? returnDiscounts[returnIndex].onwardIndex
          : [];
      returnDiscounts[returnIndex] = {
        ...returnDiscounts[returnIndex],
        onwardIndex: [
          ...onwardDiscountArr,
          {
            index: onwardIndex,
            discount: value
          }
        ]
      };
    } else if (key.indexOf('o') !== -1 && key.indexOf('r') === -1) {
      const index = parseInt(key.split('o')[1]);
      onwardDiscounts[index] = {
        discount: value
      };
    } else if (key.indexOf('o') === -1 && key.indexOf('r') !== -1) {
      const index = parseInt(key.split('r')[1]);
      returnDiscounts[index] = {
        discount: value
      };
    }
  });
  return {
    onwardDiscounts,
    returnDiscounts
  };
}

const getDiscount = (
  discountObj,
  correspondingJourneyType,
  correspondingIndex,
  showOnePaxPrice,
  totalPax
) => {
  if (discountObj[correspondingJourneyType]) {
    for (let i = 0; i < discountObj[correspondingJourneyType].length; i++) {
      const dis = discountObj[correspondingJourneyType][i];
      if (dis.index === correspondingIndex) {
        return Math.round(showOnePaxPrice ? dis.discount / totalPax : dis.discount);
      }
    }
  }
  return Math.round(showOnePaxPrice ? discountObj.discount / totalPax : discountObj.discount);
};

const checkIfInDisplay = (displayList, index) => {
  for (let i = 0; i < displayList.length; i++) {
    if (displayList[i].originalIndex === index) {
      return true;
    }
  }
  return false;
};

export function getDomesticRoundTripCombo(
  onwardFlights,
  returnFlights,
  displayOnwardCheapestFlights,
  displayReturnCheapestFlights,
  filterMinMaxValues,
  carrierFilters,
  discounts,
  roundtripComboIndexes,
  showOnePaxPrice,
  totalPax
) {
  // finding cheapest combo
  if (!displayOnwardCheapestFlights[0] || !displayReturnCheapestFlights[0]) {
    return {
      priceChangedFlag: false
    };
  }
  let cheapestPrice =
    displayOnwardCheapestFlights[0].valuesForSort.cost +
    displayReturnCheapestFlights[0].valuesForSort.cost;
  let priceChangedFlag = false;
  let onwardComboIndex = roundtripComboIndexes.onwardIndex;
  let returnComboIndex = roundtripComboIndexes.returnIndex;
  Object.entries(filterMinMaxValues.carriers).forEach(([carrier, minMaxValues]) => {
    if (carrierFilters.length === 0 || carrierFilters.indexOf(carrier) > -1) {
      if (minMaxValues['onward'] && minMaxValues['return']) {
        const onwardIndex = minMaxValues['onward'].index;
        const returnIndex = minMaxValues['return'].index;
        if (
          checkIfInDisplay(displayOnwardCheapestFlights, onwardIndex) &&
          checkIfInDisplay(displayReturnCheapestFlights, returnIndex)
        ) {
          const onwardPrice = onwardFlights[onwardIndex][0][5]
            ? onwardFlights[onwardIndex][0][5]
            : showOnePaxPrice
            ? onwardFlights[onwardIndex][0].p1
            : onwardFlights[onwardIndex][0].p;
          const onwardDiscount = discounts.onwardDiscounts[onwardIndex]
            ? getDiscount(
                discounts.onwardDiscounts[onwardIndex],
                'returnIndex',
                returnIndex,
                showOnePaxPrice,
                totalPax
              )
            : 0;
          const returnPrice = returnFlights[returnIndex][0][5]
            ? returnFlights[returnIndex][0][5]
            : showOnePaxPrice
            ? returnFlights[returnIndex][0].p1
            : returnFlights[returnIndex][0].p;
          const returnDiscount =
            discounts.returnDiscounts[returnIndex] &&
            !discounts.returnDiscounts[returnIndex].onwardIndex
              ? Math.round(
                  showOnePaxPrice
                    ? discounts.returnDiscounts[returnIndex].discount / totalPax
                    : discounts.returnDiscounts[returnIndex].discount
                )
              : 0;
          const priceForThisCombo = onwardPrice - onwardDiscount + (returnPrice - returnDiscount);
          if (priceForThisCombo < cheapestPrice) {
            cheapestPrice = priceForThisCombo;
            priceChangedFlag = true;
            onwardComboIndex = onwardIndex;
            returnComboIndex = returnIndex;
          }
        }
      }
    }
  });
  if (!priceChangedFlag) {
    if (
      onwardComboIndex !== displayOnwardCheapestFlights[0].originalIndex ||
      returnComboIndex !== displayReturnCheapestFlights[0].originalIndex
    ) {
      priceChangedFlag = true;
      onwardComboIndex = displayOnwardCheapestFlights[0].originalIndex;
      returnComboIndex = displayReturnCheapestFlights[0].originalIndex;
    }
  }
  return {
    priceChangedFlag,
    cheapestPrice,
    onwardComboIndex,
    returnComboIndex
  };
}

export const getCarriers = flts => {
  const carriersArr = [];
  for (let flt = 0; flt < flts.length; flt++) {
    const data = flts[flt];
    if (carriersArr.indexOf(data.c) === -1) {
      carriersArr.push(data.c);
    }
  }
  return carriersArr;
};

export const transformDataForFareFamilyTable = flightData => {
  const { ancillaryList, flightArr, fareFamilyOrder } = flightData;
  const { depText, depDate } = flightArr[0];
  const { arrText } = flightArr[flightArr.length - 1];
  const data = {
    headerRow: { text: 'Parameter', values: [] },
    dataRows: {},
    source: depText,
    dest: arrText,
    date: depDate && depDate.dateOnCard,
    flightInfo: [],
    selectedFareFamilyindex: null,
    fareFamilyOrder
  };
  flightArr.forEach(flights => {
    data.flightInfo.push({
      logo: flights.carrier.carrierCode,
      name: flights.carrier.carrierName,
      number: flights.flightNumber,
      depDateFormatted: dateformat(flights.depDate.dateObj, 'yyyy:mm:dd'),
      depDateObj: flights.depDate,
      depTime: flights.depTime,
      arrDateFormatted: dateformat(flights.arrDate.dateObj, 'yyyy:mm:dd'),
      arrDateObj: flights.arrDate,
      arrTime: flights.arrTime,
      durationInMin: getTotalMinutesFromDuration(flights.duration)
    });
  });
  ancillaryList.forEach((al, i) => {
    data.headerRow.values.push({
      amount: al.bundleAmtNonFormatted,
      tag: al.tag,
      header: al.header,
      persuasionText: al.persuasionText,
      currentActive: i === 0 ? true : false
    });
    if (Array.isArray(al.detailsList) && al.detailsList.length) {
      al.detailsList.forEach(dl => {
        if (dl.t) {
          const text = dl.t.split('\n').join(', ');
          if (!data.dataRows.hasOwnProperty(text)) {
            data.dataRows[text] = {
              icon: dl.i,
              text,
              values: []
            };
          }
          for (let j = 0; j < i; j++) {
            if (!data.dataRows[text].values[j]) {
              data.dataRows[text].values[j] = null;
            }
          }
          data.dataRows[text].values[i] = {
            isCommon: dl.isCommon,
            val: dl.v ? dl.v.split('\n').join(', ') : ''
          };
        }
      });
    }
    if (i === 0) {
      data.selectedFareFamilyindex = 0;
    }
  });
  return data;
};

const sortingFunctionsForGroundUp = {
  pricesLowToHigh: (a, b) => {
    return a.sort_meta.pr - b.sort_meta.pr;
  },
  pricesHighToLow: (a, b) => {
    return b.sort_meta.pr - a.sort_meta.pr;
  },
  departureTimeMorningToNight: (a, b) => {
    return a.sort_meta.dep_time - b.sort_meta.dep_time;
  },
  departureTimeNightToMorning: (a, b) => {
    return b.sort_meta.dep_time - a.sort_meta.dep_time;
  },
  duratonShortToLong: (a, b) => {
    return a.sort_meta.du - b.sort_meta.du;
  },
  duratonLongToShort: (a, b) => {
    return b.sort_meta.du - a.sort_meta.du;
  },
  bestLowToHigh: (a, b) => {
    return a.sort_meta.bs - b.sort_meta.bs;
  },
  bestHighToLow: (a, b) => {
    return b.sort_meta.bs - a.sort_meta.bs;
  }
};

export const sortListForGroundup = (listToBeSorted, sortSettings) => {
  try {
    if (sortingFunctionsForGroundUp[sortSettings.currentSortType]) {
      return sortList(sortingFunctionsForGroundUp[sortSettings.currentSortType], listToBeSorted);
    }
    return listToBeSorted;
  } catch (err) {
    sendDataToNewrelic(
      `SortFunction - ${sortSettings.currentSortType}`,
      `Failed with error: ${err}`
    );
  }
};

export const filterFunctionForGroundUp = (list, currentFilterState) => {
  try {
    if (!Object.keys(currentFilterState).length) return list;
    const filtered = list.filter(listItem => {
      const listFilters = listItem.filter || [];
      if (!Array.isArray(listFilters) || !listFilters.length) {
        return true;
      }
      return Boolean(
        listFilters.reduce((acc, value, fi) => {
          if (Array.isArray(currentFilterState[fi]) && currentFilterState[fi].length) {
            const currentReducedValue = currentFilterState[fi].reduce((reducedValue, e, i) => {
              if (e === 1) {
                reducedValue += Math.pow(2, i);
              }
              return reducedValue;
            }, 0);
            if (currentReducedValue !== 0) {
              acc &&= value & currentReducedValue;
            }
          }
          return acc;
        }, true)
      );
    });
    return filtered;
  } catch (err) {
    sendDataToNewrelic('filterFunctionForGroundUp', `Failed with error: ${err}`);
  }
};

export const getPreselectedFlightsFromMeta = thorData => {
  const { recommendation = [] } = thorData || {};
  const [onwardFlights = [], returnFlights = []] = recommendation;
  const obj = {
    preSelectedFlightsFromMeta: {
      onwardFlight: null,
      returnFlight: null
    }
  };
  if (Array.isArray(onwardFlights) && onwardFlights.length) {
    for (let i = 0; i < onwardFlights.length; i++) {
      if (onwardFlights[i].psl) {
        obj.preSelectedFlightsFromMeta.onwardFlight = onwardFlights[i];
        break;
      }
    }
  }
  if (Array.isArray(returnFlights) && returnFlights.length) {
    for (let i = 0; i < returnFlights.length; i++) {
      if (returnFlights[i].psl) {
        obj.preSelectedFlightsFromMeta.returnFlight = returnFlights[i];
        break;
      }
    }
  }

  return obj;
};
